import {
  add,
  differenceInCalendarDays,
  format,
  isBefore,
  isMonday,
  isTuesday,
  isWednesday,
  parseISO,
  set,
  startOfMonth,
  subDays,
} from 'date-fns'
import { AUG_1st, JULY_1st, JUNE, JUNE_1st, MAY_1st, OCT_1st, SEPT_1st } from '../constants'
import { formatDateTime, isBetween, jsonDateShort } from './dateTimeUtil'

import _ from 'lodash'
import { getServerDate } from './serverTimeUtil'

const MAX_PER_SESSION = 4 * 3

// List of dates on which there won't be lessons.  Use format 'yyyy-mm-dd'
const NO_LESSONS_DATES = ['2026-07-01']

const WEEKS_TEMPLATE = []

// const LESSON_NAMES = ['Fancy Footwork', 'Dynamic Doubles', 'Serve Mechanics', 'Drills and Games']
const LESSON_NAMES = []
const LESSON_NAMES_BY_DATE = {}

export const getLessonDescription = (date) => {
  const key = _.isString(date) ? date : jsonDateShort(date)
  const lessonName = LESSON_NAMES_BY_DATE[key]
  return lessonName ? `${lessonName} ` : ''
}

// let index = 0
const START_DATE = set(getServerDate(), { month: JUNE, date: 10 })
let date = new Date(START_DATE)
for (let i = 0; i < 14; i++) {
  const key = jsonDateShort(date)
  if (!_.includes(NO_LESSONS_DATES, key)) {
    const value = formatDateTime(date, 'eee MMMM d')
    WEEKS_TEMPLATE.push({ key, value })
    const lessonName = LESSON_NAMES[i % LESSON_NAMES.length]
    if (lessonName) {
      LESSON_NAMES_BY_DATE[key] = lessonName
    }
  }
  date = add(date, { weeks: 1 })
}
const END_DATE = date

const TIMES_TEMPLATE = [
  { key: 'early', value: '7:30 - 8:30pm', note: '' },
  { key: 'late', value: '8:30 - 9:30pm', note: '' },
]

export const isLastMinuteEnabledForCurrentWeek = (now) => {
  const start = set(START_DATE, { date: 1 })
  return isBetween(now, start, END_DATE) && (isMonday(now) || isTuesday(now) || isWednesday(now))
}

export const isBeginnerEarlyWeek = (lesson) => {
  let { date } = lesson
  date = _.isString(date) ? parseISO(date) : date
  return isBetween(date, MAY_1st, JUNE_1st) || isBetween(date, JULY_1st, AUG_1st) || isBetween(date, SEPT_1st, OCT_1st)
}

export const isEarlyWeek = (level, date) => {
  date = _.isString(date) ? parseISO(date) : date
  const isBeg = level === 'beg'
  const isBegEarlyWeek = isBeginnerEarlyWeek({ date })
  return (isBeg && isBegEarlyWeek) || (!isBeg && !isBegEarlyWeek)
}

const getLessonSignupStatus = (lessonDate) => {
  const now = getServerDate()

  // 1. Calculate the start of the month and the specific opening day
  const monthStart = startOfMonth(lessonDate)
  const openingDate = subDays(monthStart, 7)

  // 2. Logic Checks
  const isUpcoming = isBefore(now, set(lessonDate, { hours: 21, minutes: 0, seconds: 0, milliseconds: 0 }))
  const daysUntilOpen = differenceInCalendarDays(openingDate, now)
  const isEnabled = isUpcoming && daysUntilOpen <= 0

  // 3. Generate the Note
  // We only show the "Signups will open on..." note if it's not open yet
  let note = ''
  if (!isEnabled && isUpcoming) {
    note = `Signups will open on ${format(openingDate, 'MMMM do')}`
  } else if (!isUpcoming) {
    note = 'This lesson has already passed.'
  } else {
    note = 'Signups are currently open!'
  }

  return {
    isEnabled,
    note,
  }
}

export const getSessions = (level, stats, now) => {
  const result = []

  for (const week of WEEKS_TEMPLATE) {
    const dateKey = week.key
    const date = parseISO(week.key)
    const isBeg = level === 'beg'
    const isEarly = isEarlyWeek(level, date)
    const lessonLabel = isBeg ? '' : getLessonDescription(date)

    for (const time of TIMES_TEMPLATE) {
      const daysUntilLesson = differenceInCalendarDays(date, now)

      if (daysUntilLesson < 0) {
        // Lesson is in the past -- don't include it.
        continue
      }

      const isLastMinute = daysUntilLesson >= 0 && daysUntilLesson < 3

      if ((isEarly && time.key === 'early') || (!isEarly && time.key === 'late')) {
        const key = `${dateKey}.${isBeg ? 'beg' : 'adv'}`
        const timeslotStats = _.find(stats, (entry) => entry.key === key)
        const spotsRemaining = timeslotStats ? timeslotStats.spotsRemaining : MAX_PER_SESSION
        const spotsRemainingNote = spotsRemaining === 1 ? ' spot available' : ' spots available'
        const isFull = timeslotStats ? timeslotStats.isFull : false
        let note = `${lessonLabel}${isFull ? '(full)' : '' + spotsRemaining + spotsRemainingNote + ''}`
        note = (isLastMinute ? 'Last-minute signup - ' : '') + note

        const formattedDate = formatDateTime(date, 'eee MMM d')
        const value = `${formattedDate} @ ${isEarly ? '7:30pm' : '8:30pm'}`
        const { isEnabled, note: statusNote } = getLessonSignupStatus(date)
        if (!isEnabled) {
          note = statusNote
        }
        const disabled = isFull || !isEnabled
        result.push({ disabled, note, key: week.key, value })
      }
    }
  }

  return result
}
